// Copyright ©️ 2026 eVolve MEP, LLC

import { Fragment, useState } from 'react';

import type { ItemData } from '@aps_sdk/data-management';
import { type ProjectId, type AutodeskCloudModel, formatDate } from '@evolvemep/api-ts';
import { ActionIcon, Divider, Flex, Text, useMantineTheme } from '@mantine/core';
import { parseISO } from 'date-fns';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { useAutodeskAuth } from 'components/Autodesk/auth/useAutodeskAuth';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { UnlinkModelModal } from 'modules/Shop/ModelBrowser/UnlinkModelModal';

import { itemCanBeRendered } from './helpers';
import { useAutodeskModels } from './useAutodeskModels';

type Props = {
  projectId: ProjectId;
  selectedModel?: AutodeskCloudModel;
  onModelSelected?: (model: AutodeskCloudModel, autodeskModel: ItemData) => void;
  hideInaccessible?: boolean;
  allowUnlinking?: boolean;
  refreshProjects: () => void;
};

export const AutodeskModelList = ({
  projectId,
  selectedModel,
  onModelSelected,
  hideInaccessible,
  allowUnlinking,
  refreshProjects,
}: Props) => {
  const { autodeskAuth } = useAutodeskAuth();
  const theme = useMantineTheme();
  const [unlinking, setUnlinking] = useState<AutodeskCloudModel | null>(null);

  const { models, modelCache, loading, refetch: refetchModels } = useAutodeskModels(projectId, { hideInaccessible });

  return (
    <>
      {models?.map((m) => {
        const selected = m.urn === selectedModel?.urn;
        const autodeskModel = m.urn in modelCache ? modelCache[m.urn] : undefined;
        const selectable = isNotNil(onModelSelected) && isNotNil(autodeskModel) && itemCanBeRendered(autodeskModel);
        return (
          <Fragment key={m.urn}>
            <Flex
              p="xs"
              bg={!selectable ? 'gray.0' : selected ? `${theme.primaryColor}.0` : undefined}
              onClick={() => {
                if (selectable && !selected) {
                  onModelSelected(m, autodeskModel);
                }
              }}
              style={{
                overflow: 'hidden',
                cursor: selected || !selectable ? undefined : 'pointer',
                userSelect: 'none',
              }}
              align="center"
              justify="space-between"
              mih={80}
            >
              <Flex justify="center" direction="column">
                {autodeskModel === null && isNotNil(autodeskAuth) && (
                  <Text c="red">You do not have access to this model.</Text>
                )}
                <Text
                  key={m.urn}
                  c={isNotNil(autodeskModel) ? undefined : 'dimmed'}
                  fz={isNotNil(autodeskModel) ? undefined : 'sm'}
                  fw="bold"
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {autodeskModel?.attributes.displayName ?? m.urn}
                </Text>
                <Flex align="center" justify="space-between">
                  <Text c="dimmed" fz="sm">
                    Linked by <b>{validateUserNameOrEmail(m.createdByUser)}</b> at{' '}
                    <b>{formatDate(m.createdOn, 'P p')}</b>
                  </Text>
                </Flex>
                {isNotNil(autodeskModel) && (
                  <Flex align="center" justify="space-between">
                    <Text c="dimmed" fz="sm">
                      Last updated by <b>{autodeskModel.attributes.lastModifiedUserName}</b> at{' '}
                      <b>{formatDate(parseISO(autodeskModel.attributes.lastModifiedTime), 'P p')}</b>
                    </Text>
                  </Flex>
                )}
              </Flex>
              {allowUnlinking && (
                <AgGridStyleTooltip label="Unlink model from project" withArrow>
                  <ActionIcon onClick={() => setUnlinking(m)} loading={unlinking?.urn === m.urn} color="gray">
                    <EvolveIcon icon="Unlink" />
                  </ActionIcon>
                </AgGridStyleTooltip>
              )}
            </Flex>
            <Divider />
          </Fragment>
        );
      })}
      {models?.length === 0 && !loading && (
        <Text m="md" fz="sm" c="dimmed">
          No results found.
        </Text>
      )}
      {allowUnlinking && (
        <UnlinkModelModal
          refresh={() => {
            void refetchModels();
            refreshProjects();
          }}
          modelToUnlink={unlinking}
          onClose={() => setUnlinking(null)}
        />
      )}
    </>
  );
};
